import React, { useReducer, createContext, useEffect, useMemo } from "react"
import { message } from "antd"
import axios from "axios"
import { API_URL } from "../../utils/config"
import { login_login_user } from "../../pages/auth/types/login"
import isValidJwt from "../../utils/isValidJwt"

export enum AuthForms {
  SIGN_IN,
  FORGOT_PASSWORD,
}

export enum AuthActions {
  SIGN_IN,
  SING_OUT,
  TOGGLE_FORM,
  // Egasi №4 (2026-08-31): rol o'zgarishi qayta kirmasdan qo'llanishi uchun
  REFRESH_PERMISSIONS,
  // Boshliq (2026-09-04): bildirishnoma ovozi — tanlov state'da ham, localStorage'da ham bir zumda yangilanadi
  SET_MUSIC,
}
export interface IAuthState {
  isAuthenticated?: boolean | false
  user?: login_login_user | null
  currentForm?: AuthForms | AuthForms.SIGN_IN
  accessToken?: string | "" | null
  refreshToken?: string | "" | null
  warehouses?: any | null
}

export interface IAction {
  type: AuthActions
  payload?: IAuthState | null
}

// Buzilgan localStorage (masalan literal "undefined") modul yuklanishidayoq
// throw qilib butun ilovani oq ekran qilardi — xavfsiz parse bilan himoyalaymiz.
function safeParseStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}")
  } catch {
    return {}
  }
}

const INITIAL_STATE: IAuthState = {
  isAuthenticated: isValidJwt(localStorage.getItem("accessToken")),
  user: safeParseStoredUser(),
  currentForm: AuthForms.SIGN_IN,
  accessToken: localStorage.getItem("accessToken") || "",
  refreshToken: localStorage.getItem("refreshToken") || "",
}

function reducer(state: IAuthState, action: IAction): IAuthState {
  switch (action.type) {
    case AuthActions.SIGN_IN:
      localStorage.setItem("accessToken", action.payload?.accessToken || "")
      localStorage.setItem("refreshToken", action.payload?.refreshToken || "")
      action.payload?.user &&
        localStorage.setItem("user", JSON.stringify(action.payload?.user) || "")
      action.payload?.warehouses &&
        localStorage.setItem(
          "warehouses",
          JSON.stringify(action.payload?.warehouses) || ""
        )
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
      }
    case AuthActions.SING_OUT:
      // audit F2 (2026-09-04): chiqishda kuzatuv rejimi (tenantView), chat badge, menyu keshi ham tozalanadi —
      // aks holda keyingi kirgan (masalan hamkor admini) «Режим наблюдения» bannerini va begona badge'ni ko'rardi
      ;["accessToken", "refreshToken", "user", "tenantView", "tenantViewName", "unreadChat", "menu_prefs_cache", "lastActivityTime"]
        .forEach((k) => { try { localStorage.removeItem(k) } catch {} })
      return {
        ...INITIAL_STATE,
        isAuthenticated: false,
      }
    case AuthActions.TOGGLE_FORM:
      const formType =
        state.currentForm === AuthForms.FORGOT_PASSWORD
          ? AuthForms.SIGN_IN
          : AuthForms.FORGOT_PASSWORD
      return {
        ...state,
        currentForm: formType,
      }
    case AuthActions.SET_MUSIC: {
      // Boshliq (2026-09-04): ovoz tanlovi (0 = ovozsiz) — state va localStorage BIRGA yangilanadi,
      // aks holda keyingi REFRESH_PERMISSIONS eski qiymatni qaytarib, tanlov bekor bo'lardi
      const musicId = (action.payload as any)?.musicId
      const nextUser: any = { ...(state.user as any), musicId }
      try { localStorage.setItem("user", JSON.stringify(nextUser)) } catch {}
      return { ...state, user: nextUser }
    }
    case AuthActions.REFRESH_PERMISSIONS: {
      // Serverdan kelgan YANGI huquqlar user ichiga qo'yiladi va saqlanadi —
      // admin rol matritsasini o'zgartirsa F5 yetarli, qayta kirish shart emas
      const freshPerms = (action.payload as any)?.permissions
      if (!Array.isArray(freshPerms)) return state
      const pick = (k: string) =>
        action.payload && typeof action.payload === "object" && k in (action.payload as any)
          ? (action.payload as any)[k] ?? null
          : (state.user as any)?.[k] ?? null
      // Boshliq (2026-09-04): ovoz tanlovi FAQAT localStorage'da yangilanadi (server javobida yo'q) —
      // state'dagi eski qiymat bilan qayta yozilib, «ovozsiz» tanlovi bekor bo'lardi. Eng yangi manbani olamiz.
      let storedMusicId: any
      try { storedMusicId = JSON.parse(localStorage.getItem("user") || "{}")?.musicId } catch {}
      const updatedUser: any = {
        ...(state.user as any),
        ...(storedMusicId !== undefined ? { musicId: storedMusicId } : {}),
        permissions: freshPerms,
        // chat/toast marshrutlash uchun (kontragent-tizim userlari).
        // checkSession bu maydonni yubormaydi — mavjud qiymat saqlanadi
        // Egasi (2026-09-02): «Роли и права» GLAVNIY adminda yo'qolgan edi — MyPermissions null qaytarsa ham
        // `??` ESKI tenantType ni saqlab qolardi (hamkor tizimni 👁/🛡 bilan ko'rgandan keyin yopishib qolgan).
        // Endi: javobda kalit BOR bo'lsa (null bo'lsa ham) — server qiymati; kalit yo'q (checkSession) — eski qiymat.
        linkedCounterpartyId: pick("linkedCounterpartyId"),
        canOverridePrice: pick("canOverridePrice"),   // s85c
        canManualDiscount: pick("canManualDiscount"),   // #2072 P6
        canWholesaleSale: pick("canWholesaleSale"),     // tog'a #4741: «Продажа по оптовой цене»
        canManageLogins: pick("canManageLogins"),     // s88
        canAcceptOrders: pick("canAcceptOrders"),     // tog'a #2560: «Приём заказов партнёров»
        canConfirmReturns: pick("canConfirmReturns"), // tog'a #2560: «Подтверждение возвратов»
        canAcceptCash: pick("canAcceptCash"),         // tog'a #2515: «Приём наличных от менеджеров»
        // magazin tizimida «Клиенты» menyusi ochilishi uchun (K-bug)
        tenantType: pick("tenantType"),
        // roles-yashirish (markazlashuv) va ko'prik-chat sarlavhasi uchun
        tenantId: pick("tenantId"),
        parentName: pick("parentName"),
        // Boshliq (2026-09-05): header batareykasi — foydalanish muddati
        subscription: pick("subscription"),
      }
      try {
        localStorage.setItem("user", JSON.stringify(updatedUser))
      } catch {}
      return { ...state, user: updatedUser }
    }
    default:
      return state
  }
}

function useAuthReducer() {
  const [authState, authDispatch] = useReducer(reducer, INITIAL_STATE)
  return {
    authState,
    authDispatch,
  }
}

export const AuthContext = createContext(
  {} as ReturnType<typeof useAuthReducer>
)

export const AuthProvider: React.FunctionComponent = ({ children }) => {
  const { authState, authDispatch } = useAuthReducer()

  // Egasi №4 (2026-08-31): har ilova ochilishida huquqlar SERVERDAN yangilanadi —
  // admin «Роли и права»ni o'zgartirsa foydalanuvchiga F5 yetarli (relogin shart emas).
  // Egasi (2026-09-01): endi F5 ham shart emas — istalgan 403 «crm-refresh-perms»
  // hodisasini otadi (utils/config.ts) va huquqlar shu zahoti qayta tortiladi;
  // menyu/route'lar darhol sinxronlanadi.
  useEffect(() => {
    if (!authState.isAuthenticated) return

    const refreshPermissions = () => {
      const token = localStorage.getItem("accessToken")
      if (!token) return
      axios
        .get(`${API_URL}AccountAPI/MyPermissions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => {
          if (Array.isArray(r.data?.permissions)) {
            authDispatch({
              type: AuthActions.REFRESH_PERMISSIONS,
              payload: {
                permissions: r.data.permissions,
                linkedCounterpartyId: r.data.linkedCounterpartyId ?? null,
                tenantType: r.data.tenantType ?? null,
                tenantId: r.data.tenantId ?? null,
                parentName: r.data.parentName ?? null,
                tenantAiEnabled: r.data.tenantAiEnabled ?? true,   // Boshliq (2026-09-03, S4)
                // Boshliq (2026-09-05): header batareykasi — payload'da kalit bo'lmasa reducer eskisini (null) saqlab qolardi
                subscription: r.data.subscription ?? null,
                // s85c: narx/skidka huquqi (va login boshqaruvi) MyPermissions'dan user ichiga ko'chadi — admin ruxsat bergach qayta kirish shart emas
                canOverridePrice: r.data.canOverridePrice ?? false,
                canManualDiscount: r.data.canManualDiscount ?? null,   // #2072 P6 (eski backend bermaydi — null, sahifa avvalgi qoidaga tushadi)
                canWholesaleSale: r.data.canWholesaleSale ?? null,   // tog'a #4741
                canManageLogins: r.data.canManageLogins ?? false,
                // tog'a #2560: eski backend bermaydi — null (sahifa avvalgi qoidaga tushadi)
                canAcceptOrders: r.data.canAcceptOrders ?? null,
                canConfirmReturns: r.data.canConfirmReturns ?? null,
                canAcceptCash: r.data.canAcceptCash ?? false,   // tog'a #2515
              } as any,
            })
          }
        })
        .catch(() => {})
    }

    refreshPermissions()
    window.addEventListener("crm-refresh-perms", refreshPermissions)
    return () => window.removeEventListener("crm-refresh-perms", refreshPermissions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated])

  // tog'a 1056/1062 (2026-09-04): bitta brauzerda IKKI akkaunt — bir tabda kontragent (tr@q.com),
  // boshqa tabda SAV admin. localStorage umumiy: oxirgi kirgan akkauntning tokeni hamma
  // tabga o'tadi (utils/config.ts har so'rovda tokenni localStorage'dan o'qiydi), tab esa ekranda
  // eski foydalanuvchini ko'rsatib turadi — kontragent tabida SAV ma'lumoti + 404 lar chiqdi.
  // Endi: boshqa tabda BOSHQA foydalanuvchi bilan kirilsa (token UserId farq qilsa) shu tab
  // ogohlantirib qayta yuklanadi; boshqa tabda chiqilsa — bu tab ham login sahifasiga o'tadi.
  // Token yangilanishi (RenewAccessToken, o'sha user) — UserId bir xil, hech narsa bo'lmaydi.
  useEffect(() => {
    // tog'a #3563 2-qism: ochiq /apply sahifasi boshqa tabdagi kirish/chiqishga ergashmaydi
    if (!authState.isAuthenticated || /^\/apply(\/|$)/.test(window.location.pathname)) return
    const userIdOf = (t: string | null) => {
      try {
        if (!t) return ""
        const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")))
        return String(p?.UserId ?? "")
      } catch { return t || "" }
    }
    const mine = userIdOf(localStorage.getItem("accessToken"))
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "accessToken" && e.key !== null) return   // null = localStorage.clear()
      const now = localStorage.getItem("accessToken")
      if (!now) {
        authDispatch({ type: AuthActions.SING_OUT })
        window.location.href = "/login"
        return
      }
      if (mine && userIdOf(now) !== mine) {
        message.warning({
          content: "В другой вкладке выполнен вход под другим пользователем — страница обновляется.",
          key: "account_switched_toast",
          duration: 4,
        })
        setTimeout(() => window.location.reload(), 800)
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated])

  useEffect(() => {
    // tog'a #3460: omborda monitor (/board/<token>) — login'siz tablo; shu brauzerda eski sessiya bo'lsa ham harakatsizlik/sessiya tekshiruvi uni login sahifasiga OTMASIN
    // tog'a #3563 2-qism: /apply (hamkor arizasi) ham login'siz ochiq sahifa — xuddi tablo kabi
    if (!authState.isAuthenticated || window.location.pathname.startsWith("/board/") || /^\/apply(\/|$)/.test(window.location.pathname)) return

    // Initialize lastActivityTime if not present
    if (!localStorage.getItem("lastActivityTime")) {
      localStorage.setItem("lastActivityTime", Date.now().toString())
    }

    // Update lastActivityTime on user interactions (throttled)
    let lastThrottledTime = Date.now()
    const handleUserActivity = () => {
      const now = Date.now()
      if (now - lastThrottledTime > 3000) {
        lastThrottledTime = now
        localStorage.setItem("lastActivityTime", now.toString())
      }
    }

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
      "wheel",
    ]
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true })
    })

    // PERF-04 (2026-09-03): «tizim juda og'ir ishlamoqda» sababi shu yerda edi.
    // checkStatus har 6 soniyada ishlaydi va HAR SAFAR serverga checkSession so'rovi
    // yuborardi — javob 14 KB (butun huquqlar ro'yxati). Ya'ni har foydalanuvchi
    // daqiqasiga 10 ta so'rov, soatiga 600 ta, 140 KB/daqiqa. Ustiga har javob
    // JSON.stringify bilan solishtirilardi va sahifa qayta chizilardi — DOM hech
    // qachon tinchimasdi.
    // Endi: 30 daqiqalik harakatsizlik tekshiruvi AVVALGIDEK har 6 soniyada (u lokal,
    // tekin), serverga murojaat esa 60 soniyada bir marta. Bloklangan akkaunt yoki
    // o'zgargan huquq bir daqiqa ichida baribir aniqlanadi.
    const SERVER_CHECK_MS = 60000
    let lastServerCheck = 0
    const checkStatus = async () => {
      // 1. Check 30-minute idle inactivity timeout
      const THIRTY_MINUTES_MS = 30 * 60 * 1000
      const lastActive = Number(
        localStorage.getItem("lastActivityTime") || Date.now()
      )
      const idleTime = Date.now() - lastActive

      if (idleTime >= THIRTY_MINUTES_MS) {
        const isLoginOrLogout =
          window.location.pathname.includes("/login") ||
          window.location.pathname.includes("/logout")
        if (!isLoginOrLogout) {
          message.warning({
            content:
              "Вы были неактивны более 30 минут. Сессия завершена в целях безопасности.",
            key: "idle_timeout_toast",
            duration: 6,
          })
        }
        authDispatch({ type: AuthActions.SING_OUT })
        localStorage.clear()
        setTimeout(() => {
          window.location.href = "/login"
        }, 300)
        return
      }

      // 2. Check token and session validity with backend
      const token = localStorage.getItem("accessToken")
      if (!token) {
        authDispatch({ type: AuthActions.SING_OUT })
        return
      }

      // PERF-04: serverga murojaat 60 soniyada bir marta (lokal tekshiruvlar yuqorida bajarildi)
      const _now = Date.now()
      if (_now - lastServerCheck < SERVER_CHECK_MS) return
      lastServerCheck = _now

      try {
        const response = await axios.get(`${API_URL}AccountAPI/checkSession`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.data?.permissions) {
          // Egasi (2026-09-01): closure ichidagi authState ESKIRGAN bo'ladi —
          // ilk holat bilan solishtirish birinchi o'zgarishni qo'llab, keyingisini
          // (masalan, huquq QAYTARIB berilganini) «farq yo'q» deb o'tkazib yuborardi.
          // Har doim localStorage'dagi JORIY user bilan solishtiramiz va
          // REFRESH_PERMISSIONS orqali qo'llaymiz (reducer eng so'nggi state oladi).
          let storedPerms: any = null
          try {
            storedPerms = JSON.parse(localStorage.getItem("user") || "{}")?.permissions
          } catch {}
          const currentPermsStr = JSON.stringify(storedPerms || [])
          const newPermsStr = JSON.stringify(response.data.permissions)
          if (currentPermsStr !== newPermsStr) {
            authDispatch({
              type: AuthActions.REFRESH_PERMISSIONS,
              payload: { permissions: response.data.permissions } as any,
            })
          }
        }
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          const isLogoutRoute =
            window.location.pathname.includes("/logout") ||
            window.location.pathname.includes("/login")
          if (!isLogoutRoute) {
            const customMsg =
              err.response?.data?.message ||
              "Сессия завершена или ваш аккаунт заблокирован"
            message.error({
              content: customMsg,
              key: "session_expired_toast",
              duration: 4,
            })
          }
          authDispatch({ type: AuthActions.SING_OUT })
          localStorage.clear()
          setTimeout(() => {
            window.location.href = "/login"
          }, 500)
        }
      }
    }

    checkStatus()
    const interval = setInterval(checkStatus, 6000)

    return () => {
      clearInterval(interval)
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity)
      })
    }
  }, [authState.isAuthenticated, authDispatch])

  // PERF-06 (2026-09-03): value har renderda YANGI obyekt bo'lardi — AuthContext butun
  // ilovani o'rab turadi, shuning uchun provider qayta chizilganda uni ishlatadigan
  // HAR BIR komponent ham qayta chizilardi (sidebar, sarlavha, jadvallar). Endi obyekt
  // faqat authState haqiqatan o'zgarganda yangilanadi.
  const authCtxValue = useMemo(() => ({ authState, authDispatch }), [authState])

  return (
    <AuthContext.Provider value={authCtxValue}>
      {children}
    </AuthContext.Provider>
  )
}
